import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, Settings, Play, Square, Download, Image as ImageIcon, FileText, Presentation, Network } from 'lucide-react';
import PageHeader from './PageHeader';
import AIStoryGeneratorPanel from './AIStoryGeneratorPanel';

const LinkedInSlideCompiler = () => {
  const navigate = useNavigate();
  const [configComplete, setConfigComplete] = useState(false);
  const [config, setConfig] = useState({
    outputFormat: 'linkedin-pdf',
    background: 'gradient',
    backgroundColor: '#667eea',
    gradientStart: '#667eea',
    gradientEnd: '#764ba2',
    branding: {
      topBanner: false,
      bottomBanner: false,
      logo: null,
      logoPosition: 'top-right'
    },
    storyType: 'business',
    captureMode: 'full'
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader borderBottom={true} />
      
      {/* Action Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                LinkedIn Slide Compiler <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded">BETA</span>
              </h2>
              <p className="text-sm text-slate-600">
                Create engaging "What would YOU choose?" teasers for social media
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8">
        {!configComplete ? (
          <PreRecordConfiguration 
            config={config}
            setConfig={setConfig}
            onComplete={() => setConfigComplete(true)}
          />
        ) : (
          <RecordingArea 
            config={config}
            onBack={() => setConfigComplete(false)}
          />
        )}
      </main>
    </div>
  );
};

// Pre-Record Configuration Component
const PreRecordConfiguration = ({ config, setConfig, onComplete }) => {
  const outputFormats = [
    { id: 'linkedin-pdf', name: 'LinkedIn PDF (16:9)', aspect: '16:9', width: 1920, height: 1080 },
    { id: 'linkedin-square', name: 'LinkedIn Square (1:1)', aspect: '1:1', width: 1080, height: 1080 },
    { id: 'instagram', name: 'Instagram/Facebook (4:5)', aspect: '4:5', width: 1080, height: 1350 },
    { id: 'png-sequence', name: 'PNG Slide Sequence', aspect: '16:9', width: 1920, height: 1080 },
    { id: 'pptx', name: 'PPTX Deck', aspect: '16:9', width: 1920, height: 1080 }
  ];

  const storyTypes = [
    { id: 'business', name: 'Business Scenario' },
    { id: 'training', name: 'Training Path' },
    { id: 'error-chain', name: 'Error Chain' },
    { id: 'consumer', name: 'Consumer Story' },
    { id: 'product', name: 'Product Walkthrough' }
  ];

  const captureModes = [
    { id: 'full', name: 'Full Capture', desc: 'Capture every passage' },
    { id: 'key-slides', name: 'Key Slides Only', desc: 'Requires [slide] tag' },
    { id: 'auto-summary', name: 'Auto Summary', desc: 'Group steps automatically' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Marketing Text */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-3">
          Create Engaging Social Media Teasers
        </h2>
        <ul className="space-y-2 text-blue-800">
          <li>✓ Record a path through your interactive story</li>
          <li>✓ Export as a LinkedIn-ready slide deck</li>
          <li>✓ Add branding and custom styling</li>
          <li>✓ CTA: <strong>"Would you make different choices?"</strong></li>
        </ul>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Pre-Record Configuration</h3>

        {/* Output Format */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            1. Output Format Profile
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            {outputFormats.map(format => (
              <button
                key={format.id}
                onClick={() => setConfig({...config, outputFormat: format.id})}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  config.outputFormat === format.id
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="font-semibold text-slate-900">{format.name}</div>
                <div className="text-xs text-slate-600 mt-1">
                  {format.width} × {format.height} ({format.aspect})
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Background Options */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            2. Background Options
          </label>
          <div className="grid md:grid-cols-3 gap-3">
            <button
              onClick={() => setConfig({...config, background: 'solid'})}
              className={`p-4 rounded-lg border-2 transition-all ${
                config.background === 'solid'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300'
              }`}
            >
              <div className="font-semibold">Solid Color</div>
            </button>
            <button
              onClick={() => setConfig({...config, background: 'gradient'})}
              className={`p-4 rounded-lg border-2 transition-all ${
                config.background === 'gradient'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300'
              }`}
            >
              <div className="font-semibold">Gradient</div>
            </button>
            <button
              onClick={() => setConfig({...config, background: 'transparent'})}
              className={`p-4 rounded-lg border-2 transition-all ${
                config.background === 'transparent'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300'
              }`}
            >
              <div className="font-semibold">Transparent</div>
              <div className="text-xs text-slate-600">(PNG/PPTX only)</div>
            </button>
          </div>
        </div>

        {/* Story Type Preset */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            3. Story Type Preset
          </label>
          <div className="grid md:grid-cols-3 gap-3">
            {storyTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setConfig({...config, storyType: type.id})}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.storyType === type.id
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="font-semibold">{type.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Capture Mode */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            4. Capture Mode
          </label>
          <div className="space-y-3">
            {captureModes.map(mode => (
              <button
                key={mode.id}
                onClick={() => setConfig({...config, captureMode: mode.id})}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  config.captureMode === mode.id
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="font-semibold text-slate-900">{mode.name}</div>
                <div className="text-sm text-slate-600">{mode.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Continue Button */}
        <div className="pt-4">
          <Button
            onClick={onComplete}
            size="lg"
            className="w-full bg-indigo-600 hover:bg-indigo-700"
          >
            Continue to Recording Area
          </Button>
        </div>
      </div>
    </div>
  );
};

// Recording Area Component
const RecordingArea = ({ config, onBack }) => {
  const navigate = useNavigate();
  const textareaRef = React.useRef(null);
  const [tweeCode, setTweeCode] = useState(`:: StoryTitle
My Interactive Story

:: Start
Welcome to your story!

This is where your journey begins.

[[Make a choice|Choice1]]
[[Go another way|Choice2]]

:: Choice1
You chose the first path.

This is the result of your decision.

[[Continue|End]]

:: Choice2
You took the second path.

A different outcome awaits.

[[Continue|End]]

:: End
The story concludes here.

Thank you for reading!`);
  const [isRecording, setIsRecording] = useState(false);
  const [capturedSlides, setCapturedSlides] = useState([]);
  const [compiledHtml, setCompiledHtml] = useState('');
  const [layoutOption, setLayoutOption] = useState('bottom');
  const [linkDisplay, setLinkDisplay] = useState('buttons');
  const [boldColor, setBoldColor] = useState('#1e293b');
  const [italicColor, setItalicColor] = useState('#64748b');
  const [headingColor, setHeadingColor] = useState('#667eea');
  const [linkColor, setLinkColor] = useState('#667eea');
  const [isCompiling, setIsCompiling] = useState(false);
  const iframeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);

  // Get canvas dimensions from config
  const getCanvasDimensions = () => {
    const formats = {
      'linkedin-pdf': { width: 1920, height: 1080 },
      'linkedin-square': { width: 1080, height: 1080 },
      'instagram': { width: 1080, height: 1350 },
      'png-sequence': { width: 1920, height: 1080 },
      'pptx': { width: 1920, height: 1080 }
    };
    return formats[config.outputFormat] || { width: 1920, height: 1080 };
  };

  const dimensions = getCanvasDimensions();
  const aspectRatio = dimensions.width / dimensions.height;

  // Calculate scale to fit iframe in container
  React.useEffect(() => {
    const updateScale = () => {
      if (containerRef.current && compiledHtml) {
        const containerWidth = containerRef.current.offsetWidth;
        const scale = containerWidth / dimensions.width;
        setScale(scale);
        
        if (iframeRef.current) {
          iframeRef.current.style.transform = `scale(${scale})`;
        }
      }
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [compiledHtml, dimensions.width]);

  const handleAIStoryGenerated = (generatedTwee) => {
    setTweeCode(generatedTwee);
  };

  const handleCompile = () => {
    setIsCompiling(true);
    
    setTimeout(() => {
      try {
        // Use the full-featured story HTML generation
        const html = generateStoryHtml(tweeCode, layoutOption, {
          boldColor,
          italicColor, 
          headingColor,
          linkColor
        }, linkDisplay);
        setCompiledHtml(html);
        setIsCompiling(false);
      } catch (error) {
        console.error('Compilation error:', error);
        alert('Error compiling story. Please check your Twee syntax.');
        setIsCompiling(false);
      }
    }, 500);
  };
  
  // Format insertion helpers
  const insertAtCursor = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = tweeCode;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setTweeCode(newText);
    
    setTimeout(() => {
      const newPos = start + before.length + selectedText.length;
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleStartRecording = () => {
    if (!compiledHtml) {
      alert('Please compile your story first!');
      return;
    }
    setIsRecording(true);
    setCapturedSlides([]);
  };

  const handleStopRecording = () => {
    setIsRecording(false);
  };

  const handleCaptureSlide = () => {
    // Capture current iframe content
    const slideData = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      imageData: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==` // Placeholder
    };
    setCapturedSlides([...capturedSlides, slideData]);
  };

  const handleSendToVisualEditor = () => {
    sessionStorage.setItem('imported_twee', tweeCode);
    navigate('/visual-editor');
  };

  const handleExport = (format) => {
    alert(`Export as ${format} - Feature coming soon!`);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Control Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <Button onClick={onBack} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Config
          </Button>
          
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isRecording 
                ? 'bg-red-100 text-red-700 animate-pulse' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              {isRecording ? '● Recording' : '○ Ready'}
            </div>
            
            {!isRecording ? (
              <Button 
                onClick={handleStartRecording}
                className="bg-green-600 hover:bg-green-700"
                disabled={!compiledHtml}
              >
                <Play className="h-4 w-4 mr-2" />
                Start Recording
              </Button>
            ) : (
              <Button 
                onClick={handleStopRecording}
                variant="destructive"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop Recording
              </Button>
            )}

            <Button 
              onClick={handleCaptureSlide}
              variant="outline"
              disabled={!isRecording}
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Capture Slide
            </Button>
          </div>

          <div className="text-sm text-slate-600">
            Slides: {capturedSlides.length}
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor Column */}
        <div className="space-y-4">
          {/* AI Story Generator */}
          <AIStoryGeneratorPanel 
            onStoryGenerated={handleAIStoryGenerated}
          />

          {/* Twee Editor */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-100 border-b border-slate-200 px-4 py-3">
              <span className="font-semibold text-slate-700">Twee Code Editor</span>
            </div>
            
            {/* Story Options */}
            <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-3">
              <span className="text-sm text-indigo-700 font-medium block mb-3">Story Options</span>
              
              {/* Link Display Choice */}
              <div className="mb-3">
                <label className="text-xs text-indigo-600 font-medium block mb-2">Link Display:</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLinkDisplay('buttons')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      linkDisplay === 'buttons'
                        ? 'bg-indigo-600 text-white shadow-md' 
                        : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                    }`}
                    type="button"
                  >
                    🔘 Buttons
                  </button>
                  <button
                    onClick={() => setLinkDisplay('inline')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      linkDisplay === 'inline'
                        ? 'bg-indigo-600 text-white shadow-md' 
                        : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                    }`}
                    type="button"
                  >
                    📝 Inline
                  </button>
                </div>
              </div>
              
              {/* Button Placement */}
              {linkDisplay === 'buttons' && (
                <div>
                  <label className="text-xs text-indigo-600 font-medium block mb-2">Button Placement:</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLayoutOption('bottom')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        layoutOption === 'bottom'
                          ? 'bg-indigo-600 text-white shadow-md' 
                          : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                      }`}
                      type="button"
                    >
                      ↓ Bottom
                    </button>
                    <button
                      onClick={() => setLayoutOption('side')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        layoutOption === 'side'
                          ? 'bg-indigo-600 text-white shadow-md' 
                          : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                      }`}
                      type="button"
                    >
                      → Side
                    </button>
                    <button
                      onClick={() => setLayoutOption('leftRight')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        layoutOption === 'leftRight'
                          ? 'bg-indigo-600 text-white shadow-md' 
                          : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-100'
                      }`}
                      type="button"
                    >
                      ← L/R →
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Formatting Toolbar */}
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => insertAtCursor('**', '**')}
                  className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                  title="Bold"
                  type="button"
                >
                  <strong>B</strong>
                </button>
                <button
                  onClick={() => insertAtCursor('//', '//')}
                  className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                  title="Italic"
                  type="button"
                >
                  <em>I</em>
                </button>
                <button
                  onClick={() => insertAtCursor('## ')}
                  className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                  title="Heading"
                  type="button"
                >
                  H2
                </button>
                <button
                  onClick={() => insertAtCursor('[[',' ]]')}
                  className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-100"
                  title="Link"
                  type="button"
                >
                  [[Link]]
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <textarea
                ref={textareaRef}
                value={tweeCode}
                onChange={(e) => setTweeCode(e.target.value)}
                className="w-full h-64 p-3 font-mono text-sm border border-slate-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Paste your Twee code here..."
              />
            </div>
            
            <div className="border-t border-slate-200 p-4 flex gap-3">
              <Button 
                onClick={handleCompile} 
                className="flex-1"
                disabled={isCompiling}
              >
                {isCompiling ? 'Compiling...' : 'Compile Story'}
              </Button>
              <Button 
                onClick={handleSendToVisualEditor}
                variant="outline"
                className="flex-1"
              >
                <Network className="h-4 w-4 mr-2" />
                Visual Editor
              </Button>
            </div>
          </div>

          {/* Configuration Summary */}
          <div className="bg-slate-50 rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-slate-700 mb-2">Recording Configuration</h4>
            <ul className="space-y-1 text-slate-600">
              <li>• Format: {config.outputFormat}</li>
              <li>• Canvas: {dimensions.width}×{dimensions.height}</li>
              <li>• Background: {config.background}</li>
              <li>• Story Type: {config.storyType}</li>
              <li>• Capture: {config.captureMode}</li>
            </ul>
          </div>
        </div>

        {/* Preview Column */}
        <div className="space-y-4">
          {/* Recording Canvas */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-semibold text-slate-700">Recording Canvas</span>
              <span className="text-xs text-slate-500">{dimensions.width}×{dimensions.height}</span>
            </div>
            
            <div 
              ref={containerRef}
              className="border-2 border-slate-300 rounded-lg overflow-hidden"
              style={{
                width: '100%',
                paddingBottom: `${(dimensions.height / dimensions.width) * 100}%`,
                position: 'relative',
                background: config.background === 'gradient' 
                  ? `linear-gradient(135deg, ${config.gradientStart || '#667eea'} 0%, ${config.gradientEnd || '#764ba2'} 100%)`
                  : config.background === 'transparent'
                  ? 'transparent'
                  : config.backgroundColor || '#667eea'
              }}
            >
              {compiledHtml ? (
                <iframe
                  ref={iframeRef}
                  srcDoc={compiledHtml}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${dimensions.width}px`,
                    height: `${dimensions.height}px`,
                    transform: 'scale(1)',
                    transformOrigin: 'top left',
                    border: 'none'
                  }}
                  title="Story Preview"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <Settings className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Compile your story to start recording</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Captured Slides Preview */}
          {capturedSlides.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold text-slate-700">Captured Slides ({capturedSlides.length})</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleExport('pdf')}>
                    <FileText className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleExport('png')}>
                    <ImageIcon className="h-4 w-4 mr-1" />
                    PNG
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleExport('pptx')}>
                    <Presentation className="h-4 w-4 mr-1" />
                    PPTX
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                {capturedSlides.map((slide, index) => (
                  <div key={slide.id} className="relative border border-slate-200 rounded overflow-hidden aspect-video bg-slate-100">
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
                      Slide {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Parse Twee format - helper function
const parseTwee = (twee) => {
  const passages = [];
  const lines = twee.split('\n');
  let currentPassage = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.trim().startsWith(':: ')) {
      if (currentPassage && currentPassage.content.trim()) {
        passages.push(currentPassage);
      }
      const name = line.substring(3).split('[')[0].trim();
      currentPassage = { name, content: '' };
    } else if (currentPassage) {
      // Add all lines including empty ones to preserve formatting
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage && currentPassage.content.trim()) {
    passages.push(currentPassage);
  }
  
  return passages;
};

// Slide-optimized story HTML generator for LinkedIn Slide Compiler
const generateStoryHtml = (twee, layoutOption = 'bottom', styleColors = {}, linkDisplay = 'buttons') => {
  // Parse Twee format
  const passages = parseTwee(twee);
  let startPassage = passages.find(p => p.name === 'Start');
  const storyTitle = passages.find(p => p.name === 'StoryTitle')?.content.trim() || 'Interactive Story';
  
  // Extract style colors with defaults
  const { 
    boldColor = '#fbd38d', 
    italicColor = '#b794f4', 
    headingColor = '#90cdf4', 
    linkColor = '#667eea' 
  } = styleColors;
  
  // If no Start passage, use the first non-StoryTitle passage
  if (!startPassage) {
    startPassage = passages.find(p => p.name !== 'StoryTitle');
  }
  
  if (!startPassage) {
    throw new Error('No valid passages found. Make sure you have a ":: Start" passage.');
  }

  // Filter out StoryTitle from passages used in navigation
  const storyPassages = passages.filter(p => p.name !== 'StoryTitle');
  
  // Ensure Start passage is first for CSS :first-of-type to work
  storyPassages.sort((a, b) => {
    if (a.name === startPassage.name) return -1;
    if (b.name === startPassage.name) return 1;
    return 0;
  });

  // Helper function to process content with formatting and images
  const processContent = (text) => {
    let processed = text;
    
    // Process Twee image syntax
    processed = processed.replace(/\[img\[([^\]|]+)(?:\|([^\]]+))?\]\]/gi, (match, url, position) => {
      const pos = position ? position.toLowerCase().trim() : 'center';
      return `<img src="${url}" alt="Story image" class="story-image img-${pos}">`;
    });
    
    // Process Markdown images
    processed = processed.replace(/!\[([^\]]*)\]\(([^)|]+)(?:\|([^)]+))?\)/gi, (match, alt, url, position) => {
      const pos = position ? position.toLowerCase().trim() : 'center';
      return `<img src="${url}" alt="${alt}" class="story-image img-${pos}">`;
    });
    
    // Horizontal rules: convert --- or *** to <hr>
    processed = processed.replace(/^[\-\*]{3,}$/gm, '<hr>');
    
    // Convert bullet lists BEFORE bold/italic to avoid conflicts
    const lines = processed.split('\n');
    let inList = false;
    let result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const bulletMatch = line.match(/^[\*\-]\s+(.+)$/);
      
      if (bulletMatch && !line.match(/^[\*\-]{2,}/)) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${bulletMatch[1]}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }
    if (inList) {
      result.push('</ul>');
    }
    processed = result.join('\n');
    
    // SUGARCUBE SYNTAX (PRIMARY) - Bold: **text**
    processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // SUGARCUBE SYNTAX - Italic: *text* (not in lists or strong tags)
    processed = processed.replace(/(?<!<li>|<strong>)\*([^*\n]+)\*(?!<\/strong>)/g, '<em>$1</em>');
    processed = processed.replace(/(?<!<strong>)_([^_\n]+)_(?!<\/strong>)/g, '<em>$1</em>');
    
    // SUGARCUBE SYNTAX - Strikethrough: ~~text~~
    processed = processed.replace(/~~([^~]+)~~/g, '<del>$1</del>');
    
    // SUGARCUBE SYNTAX - Headings: !Heading (SugarCube native)
    processed = processed.replace(/^!{1,6}\s*(.+)$/gm, (match, text) => {
      const level = match.match(/^!+/)[0].length;
      return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
    });
    
    // MARKDOWN SYNTAX - Headings: # text (also supported)
    processed = processed.replace(/^#{1,6}\s+(.+)$/gm, (match, text) => {
      const level = match.match(/^#+/)[0].length;
      return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
    });
    
    // LEGACY TWINE 1 SYNTAX (BACKWARD COMPATIBILITY) - Bold: ''text''
    processed = processed.replace(/''([^']+)''/g, '<strong>$1</strong>');
    
    // LEGACY TWINE 1 SYNTAX - Italic: //text//
    processed = processed.replace(/\/\/([^/]+)\/\//g, '<em>$1</em>');
    
    // LEGACY TWINE 1 SYNTAX - Underline: __text__ (when not used for italic)
    processed = processed.replace(/__([^_]+)__/g, '<u>$1</u>');
    
    // LEGACY TWINE 1 SYNTAX - Strikethrough: ==text==
    processed = processed.replace(/==([^=]+)==/g, '<del>$1</del>');
    
    // Clean up excessive blank lines
    processed = processed.replace(/\n{4,}/g, '\n\n\n');
    
    // Convert newlines to breaks smartly
    processed = processed.replace(/\n{2,3}/g, '<br><br>');
    processed = processed.replace(/\n/g, '<br>');
    
    return processed;
  };

  // Generate slide-optimized HTML (fixed dimensions, no responsive units)
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=1920, height=1080, initial-scale=1.0">
    <title>${storyTitle}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
            width: 1920px;
            height: 1080px;
            margin: 0;
            padding: 0;
            overflow: hidden;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            line-height: 1.5;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 40px;
        }
        
        /* Story Container - Fixed size for canvas */
        #story-container {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            display: flex;
            flex-direction: column;
            width: 1840px;
            height: 1000px;
            overflow: hidden;
        }
        
        /* Story Header - Fixed sizing */
        .story-header {
            padding: 24px 40px 16px;
            border-bottom: 1px solid #e2e8f0;
            flex-shrink: 0;
        }
        .story-title {
            color: #667eea;
            font-size: 48px;
            font-weight: 700;
            margin-bottom: 8px;
            line-height: 1.2;
        }
        
        /* Navigation Controls - Fixed sizing */
        #nav-controls {
            display: flex;
            align-items: center;
            gap: 16px;
            font-size: 16px;
            color: #64748b;
            margin-bottom: 8px;
        }
        .nav-link {
            cursor: pointer;
            color: #667eea;
            text-decoration: none;
            transition: all 0.2s;
        }
        .nav-link:hover {
            color: #5568d3;
            text-decoration: underline;
        }
        .nav-link.disabled {
            color: #cbd5e1;
            cursor: not-allowed;
            pointer-events: none;
        }
        .nav-separator {
            color: #cbd5e1;
        }
        
        /* Breadcrumb - Fixed sizing */
        #breadcrumb {
            font-size: 14px;
            color: #64748b;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        }
        .breadcrumb-item {
            cursor: pointer;
            color: #667eea;
            text-decoration: none;
        }
        .breadcrumb-item:hover {
            text-decoration: underline;
        }
        .breadcrumb-sep {
            color: #cbd5e1;
        }
        
        /* Content Area - Fixed sizing, centered */
        .story-content-wrapper {
            flex: 1;
            overflow: hidden;
            padding: 50px 60px;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        /* Passage Display */
        .passage {
            display: none;
            height: 100%;
        }
        .passage.active {
            display: flex;
            flex-direction: column;
            justify-content: center;
            animation: fadeIn 0.3s ease-in;
            height: 100%;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .passage-title {
            display: none;
        }
        .passage-content {
            color: #2d3748;
            font-size: 28px;
            margin-bottom: 30px;
            line-height: 1.6;
            text-align: center;
        }
        .passage-content h3, .passage-content h4, .passage-content h5, .passage-content h6 {
            color: ${headingColor};
            font-weight: 600;
            margin: 24px 0 16px 0;
        }
        .passage-content h3 { font-size: 42px; }
        .passage-content h4 { font-size: 36px; }
        .passage-content h5 { font-size: 32px; }
        .passage-content strong {
            font-weight: 700;
            color: ${boldColor};
        }
        .passage-content em {
            font-style: italic;
            color: ${italicColor};
        }
        .passage-content u {
            text-decoration: underline;
        }
        .passage-content del {
            text-decoration: line-through;
            opacity: 0.7;
        }
        .passage-content hr {
            border: none;
            border-top: 2px solid #e2e8f0;
            margin: 24px 0;
        }
        .passage-content ul {
            margin: 16px 0;
            padding-left: 40px;
            line-height: 1.4;
        }
        .passage-content li {
            margin: 8px 0;
            line-height: 1.4;
        }
        .passage-content br + br {
            display: block;
            content: "";
            margin-top: 0.5em;
        }
        
        /* Image Styling - Fixed sizing for slides */
        .story-image {
            max-width: 100%;
            max-height: 400px;
            height: auto;
            border-radius: 8px;
            margin: 24px auto;
            display: block;
            object-fit: contain;
        }
        
        /* Image Positioning */
        .img-top, .img-bottom, .img-center {
            margin: 24px auto;
            max-width: 1200px;
            max-height: 350px;
        }
        .img-left {
            float: left;
            max-width: 600px;
            max-height: 300px;
            margin: 0 30px 16px 0;
        }
        .img-right {
            float: right;
            max-width: 600px;
            max-height: 300px;
            margin: 0 0 16px 30px;
        }
        
        /* Passage Links Footer - Fixed sizing */
        .passage-links-footer {
            padding: 24px 40px;
            border-top: 1px solid #e2e8f0;
            background: #f8fafc;
            border-radius: 0 0 16px 16px;
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            min-height: 120px;
            max-height: 160px;
        }
        
        /* Slide layouts removed - always centered for optimal capture */
        .passage-link {
            display: inline-block;
            background: ${linkColor};
            color: white;
            padding: 16px 32px;
            border-radius: 8px;
            text-decoration: none;
            transition: all 0.2s;
            font-weight: 600;
            cursor: pointer;
            border: none;
            font-family: inherit;
            font-size: 22px;
            text-align: center;
            white-space: normal;
            max-width: 500px;
        }
        .passage-link:hover {
            background: ${linkColor};
            filter: brightness(0.85);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        .passage-link:active {
            transform: translateY(0);
        }
        
        /* Inline passage links */
        .inline-passage-link {
            font-weight: 600;
            transition: all 0.2s;
            border-bottom: 2px solid currentColor;
        }
        .inline-passage-link:hover {
            opacity: 0.8;
            border-bottom-width: 3px;
        }
        
        /* Clear floats after content */
        .passage-content::after {
            content: "";
            display: table;
            clear: both;
        }
        
        /* Ensure everything fits without scrolling */
        ::-webkit-scrollbar {
            display: none;
        }
        * {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
    </style>
</head>
<body>
    <div id="story-container">
        <!-- Story Header -->
        <div class="story-header">
            <div class="story-title">${storyTitle}</div>
            <div id="nav-controls">
                <a class="nav-link" id="link-back" onclick="goBack()">← Back</a>
                <span class="nav-separator">|</span>
                <a class="nav-link" id="link-forward" onclick="goForward()">Forward →</a>
                <span class="nav-separator">|</span>
                <a class="nav-link" id="link-reset" onclick="resetStory()">⟲ Reset</a>
            </div>
            <div id="breadcrumb"></div>
        </div>
        
        <!-- Content Area (No scrolling) -->
        <div class="story-content-wrapper">
        ${storyPassages.map((passage, index) => {
          const passageId = passage.name.replace(/\s+/g, '-');
          const isStart = index === 0;
          
          // Extract links from content
          const linkRegex = /\[\[([^\]]+)\]\]/g;
          const links = [];
          let match;
          
          while ((match = linkRegex.exec(passage.content)) !== null) {
            const linkText = match[1];
            // Support both SugarCube [[Text|Passage]] and Twine [[Text->Passage]] formats
            let parts, label, target;
            if (linkText.includes('|')) {
              // SugarCube format: [[Display Text|PassageName]]
              parts = linkText.split('|');
              label = parts[0].trim();
              target = parts[1].trim();
            } else if (linkText.includes('->')) {
              // Twine format: [[Display Text->PassageName]]
              parts = linkText.split('->');
              label = parts[0].trim();
              target = parts[1].trim();
            } else {
              // Simple format: [[PassageName]]
              label = linkText.trim();
              target = linkText.trim();
            }
            
            // Check for positioning markers in label: {{{left}}} or {{{right}}}
            let position = 'default';
            let cleanLabel = label;
            
            if (label.includes('{{{left}}}')) {
              position = 'left';
              cleanLabel = label.replace('{{{left}}}', '').trim();
            } else if (label.includes('{{{right}}}')) {
              position = 'right';
              cleanLabel = label.replace('{{{right}}}', '').trim();
            }
            
            // Check if target is a URL (starts with URL:)
            const isExternalLink = target.startsWith('URL:');
            const finalTarget = isExternalLink ? target.substring(4) : target.replace(/\s+/g, '-');
            links.push({ label: cleanLabel, target: finalTarget, isExternal: isExternalLink, position });
          }
          
          let content, linksHtml;
          
          if (linkDisplay === 'inline') {
            // Keep links in the text as inline clickable links
            content = passage.content.replace(linkRegex, (match, linkText) => {
              let parts, label, target, position = 'default';
              
              // Parse link format
              if (linkText.includes('|')) {
                parts = linkText.split('|');
                label = parts[0].trim();
                target = parts[1].trim();
              } else if (linkText.includes('->')) {
                parts = linkText.split('->');
                label = parts[0].trim();
                target = parts[1].trim();
              } else {
                label = linkText.trim();
                target = linkText.trim();
              }
              
              // Remove position markers
              if (label.includes('{{{left}}}')) {
                label = label.replace('{{{left}}}', '').trim();
              } else if (label.includes('{{{right}}}')) {
                label = label.replace('{{{right}}}', '').trim();
              }
              
              // Handle external links
              const isExternalLink = target.startsWith('URL:');
              const finalTarget = isExternalLink ? target.substring(4) : target.replace(/\s+/g, '-');
              
              if (isExternalLink) {
                return `<a href="${finalTarget}" class="inline-passage-link" style="color: ${linkColor}; text-decoration: underline; cursor: pointer;">${label}</a>`;
              }
              return `<a href="#" class="inline-passage-link" data-target="${finalTarget}" onclick="navigate('${finalTarget}'); return false;" style="color: ${linkColor}; text-decoration: underline; cursor: pointer;">${label}</a>`;
            });
            
            content = processContent(content.trim());
            linksHtml = ''; // No separate links for inline mode
            
          } else {
            // Button mode: Remove links from content and show as separate buttons
            content = passage.content.replace(linkRegex, '').trim();
            
            // Remove lines that were ONLY bullet + link (now empty after link removal)
            content = content.split('\n').filter(line => {
              const trimmed = line.trim();
              return trimmed !== '' && trimmed !== '*' && trimmed !== '-';
            }).join('\n');
            
            content = processContent(content);
            
            linksHtml = links.map(link => {
              const positionAttr = `data-position="${link.position}"`;
              if (link.isExternal) {
                return `<a href="${link.target}" class="passage-link" ${positionAttr} style="text-decoration: none;">${link.label}</a>`;
              }
              return `<button class="passage-link" ${positionAttr} onclick="navigate('${link.target}')">${link.label}</button>`;
            }).join('');
          }
          
          return `
        <div class="passage" id="${passageId}" data-name="${passage.name}"${isStart ? ' data-start="true"' : ''}>
            <div class="passage-content">${content}</div>
            <div class="passage-links-data" style="display:none;">${linksHtml}</div>
        </div>`;
        }).join('\n')}
        </div>
        
        <!-- Passage Links Footer -->
        <div class="passage-links-footer" id="passage-links-container">
            <!-- Links will be dynamically inserted here -->
        </div>
    </div>

    <script>
        // Navigation history
        let history = [];
        let currentIndex = -1;
        
        // Initialize
        function init() {
            const startPassage = document.querySelector('[data-start="true"]');
            if (startPassage) {
                showPassage(startPassage.id, false);
            } else {
                // Fallback: show first passage
                const firstPassage = document.querySelector('.passage');
                if (firstPassage) {
                    showPassage(firstPassage.id, false);
                }
            }
        }
        
        // Show specific passage
        function showPassage(passageId, addToHistory = true) {
            // Hide all passages
            document.querySelectorAll('.passage').forEach(p => p.classList.remove('active'));
            
            // Show target passage
            const passage = document.getElementById(passageId);
            if (passage) {
                passage.classList.add('active');
                
                // Update history
                if (addToHistory) {
                    currentIndex++;
                    history = history.slice(0, currentIndex);
                    history.push(passageId);
                }
                
                // Update passage links in footer
                updatePassageLinks(passage);
                
                updateBreadcrumb();
                updateButtons();
                
                // Scroll content to top
                const contentWrapper = document.querySelector('.story-content-wrapper');
                if (contentWrapper) {
                    contentWrapper.scrollTop = 0;
                }
            }
        }
        
        // Update passage links in footer
        function updatePassageLinks(passage) {
            const linksData = passage.querySelector('.passage-links-data');
            const linksContainer = document.getElementById('passage-links-container');
            
            if (linksData) {
                linksContainer.innerHTML = linksData.innerHTML;
            } else {
                linksContainer.innerHTML = '';
            }
        }
        
        // Navigate to passage
        function navigate(passageId) {
            showPassage(passageId, true);
        }
        
        // Go back
        function goBack() {
            if (currentIndex > 0) {
                currentIndex--;
                showPassage(history[currentIndex], false);
            }
        }
        
        // Go forward
        function goForward() {
            if (currentIndex < history.length - 1) {
                currentIndex++;
                showPassage(history[currentIndex], false);
            }
        }
        
        // Reset to start
        function resetStory() {
            history = [];
            currentIndex = -1;
            init();
        }
        
        // Update breadcrumb
        function updateBreadcrumb() {
            const breadcrumb = document.getElementById('breadcrumb');
            if (!breadcrumb) return;
            
            const items = history.slice(Math.max(0, currentIndex - 3), currentIndex + 1);
            
            breadcrumb.innerHTML = items.map((passageId, index) => {
                const passage = document.getElementById(passageId);
                const name = passage ? passage.dataset.name : passageId;
                const isLast = index === items.length - 1;
                const actualIndex = Math.max(0, currentIndex - 3) + index;
                
                let html = '<a class="breadcrumb-item" onclick="jumpTo(' + actualIndex + ')" ';
                html += 'style="' + (isLast ? 'font-weight:600;' : '') + '">';
                html += name + '</a>';
                if (!isLast) {
                    html += '<span class="breadcrumb-sep">›</span>';
                }
                return html;
            }).join('');
        }
        
        // Jump to history index
        function jumpTo(index) {
            currentIndex = index;
            showPassage(history[currentIndex], false);
        }
        
        // Update button states
        function updateButtons() {
            const backLink = document.getElementById('link-back');
            const forwardLink = document.getElementById('link-forward');
            
            if (currentIndex <= 0) {
                backLink.classList.add('disabled');
            } else {
                backLink.classList.remove('disabled');
            }
            
            if (currentIndex >= history.length - 1) {
                forwardLink.classList.add('disabled');
            } else {
                forwardLink.classList.remove('disabled');
            }
        }
        
        // Initialize on load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    </script>
</body>
</html>`;
};

export default LinkedInSlideCompiler;
